import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { authApi } from '@/api/auth'
import router from '@/router'
import type {
  User,
  AuthResponse,
  OtpRequest,
  ApiResponse,
  UpdateProfileRequest,
} from '@/interfaces'
import { isTokenExpired } from '@/utils/jwt'
import { parseApiError, parseFieldError } from '@/utils/errorHandler'

const ACCESS_TOKEN_KEY = 'customer_access_token'
const REFRESH_TOKEN_KEY = 'customer_refresh_token'

const saveRefreshToken = (token: string): void => {
  localStorage.setItem(REFRESH_TOKEN_KEY, token)
  localStorage.removeItem('customer_refresh_token_expiry')
}

const getRefreshToken = (): string | null => {
  return localStorage.getItem(REFRESH_TOKEN_KEY)
}

const clearRefreshToken = (): void => {
  localStorage.removeItem(REFRESH_TOKEN_KEY)
  localStorage.removeItem('customer_refresh_token_expiry')
}

export const useAuthStore = defineStore('auth', () => {
  const getStoredAccessToken = (): string | null => {
    // Prefer localStorage so user stays logged in across tabs and after browser close
    const token = localStorage.getItem(ACCESS_TOKEN_KEY) || sessionStorage.getItem(ACCESS_TOKEN_KEY)
    if (token && !isTokenExpired(token)) {
      return token
    }
    if (token) {
      localStorage.removeItem(ACCESS_TOKEN_KEY)
      sessionStorage.removeItem(ACCESS_TOKEN_KEY)
    }
    return null
  }

  const accessToken = ref<string | null>(getStoredAccessToken())

  let storedUser: User | null = null
  try {
    const raw = localStorage.getItem('user')
    if (raw) storedUser = JSON.parse(raw) as User
  } catch {
    // ignore invalid JSON
  }
  const user = ref<User | null>(storedUser)

  const activeReservationId = ref<string | null>(localStorage.getItem('active_reservation_id'))

  const isAuthenticated = computed(() => {
    if (accessToken.value && user.value) return true
    return !!user.value
  })

  const fullUserName = computed(() => {
    if (user.value) {
      return `${user.value.first_name} ${user.value.last_name || ''}`.trim()
    }
    return ''
  })

  function setTokens(token: string, userData: User, refreshToken?: string): void {
    accessToken.value = token
    localStorage.setItem(ACCESS_TOKEN_KEY, token)
    sessionStorage.setItem(ACCESS_TOKEN_KEY, token)

    user.value = userData

    if (refreshToken) {
      saveRefreshToken(refreshToken)
    }

    localStorage.setItem('user', JSON.stringify(userData))
  }

  function clearTokens(): void {
    accessToken.value = null
    user.value = null
    localStorage.removeItem(ACCESS_TOKEN_KEY)
    sessionStorage.removeItem(ACCESS_TOKEN_KEY)
    clearRefreshToken()
    localStorage.removeItem('user')
    localStorage.removeItem('active_reservation_id')
  }

  async function sendOTP(data: OtpRequest): Promise<void> {
    try {
      await authApi.generateOTP(data)
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to send OTP')
    }
  }

  async function verifyOTP(data: OtpRequest & { otp: string }): Promise<void> {
    try {
      const response = await authApi.verifyOTP(data)

      if (response.error) {
        const errorMsg =
          typeof response.error === 'string'
            ? response.error
            : response.error.mobile_no?.[0] || response.error.otp?.[0] || 'OTP verification failed'
        throw new Error(errorMsg)
      }

      const tokenData = response as any

      if (typeof tokenData === 'string') {
        throw new Error(tokenData.trim())
      }

      if (tokenData?.error) {
        const errorMsg =
          typeof tokenData.error === 'string'
            ? tokenData.error
            : tokenData.error.message || 'An error occurred'
        throw new Error(errorMsg)
      }

      if (tokenData?.customer_access_token && tokenData?.user_data) {
        const userData = { ...tokenData.user_data } as User
        if (!userData.mobile_no && data.mobile_no) {
          userData.mobile_no = data.mobile_no
        }
        setTokens(tokenData.customer_access_token, userData, tokenData.refresh_token)

        // Persist active reservation from generate_token.
        // Backend responses are not fully consistent:
        // - newer shape: { active_reservation: { active_reservation_id, tenant_id, table_no, ... } }
        // - legacy shape: { active_reservation_id, ... }
        const ar = tokenData?.active_reservation
        const nestedReservationId =
          ar?.active_reservation_id != null || ar?.id != null
            ? String(ar.active_reservation_id ?? ar.id)
            : null
        const topLevelReservationId =
          tokenData?.active_reservation_id != null ? String(tokenData.active_reservation_id) : null
        const reservationId = nestedReservationId ?? topLevelReservationId

        if (reservationId && ar?.tenant_id && ar?.table_no) {
          const reservationNumber = ar.reservation_number ?? ar.reservation_numer
          setActiveReservation(reservationId, {
            tenantId: ar.tenant_id,
            tableNo: ar.table_no,
            ...(reservationNumber ? { reservationNumber: String(reservationNumber) } : {}),
          })
        } else if (reservationId) {
          // Keep the reservation id even if generate_token omitted table metadata.
          // Smart-dine-in login can hydrate the rest from check_active_reservation.
          setActiveReservation(reservationId)
        } else {
          // No active reservation in generate_token → clear stored so menu can call
          // make_instant_registered_reservation only for genuine non-owners.
          clearActiveReservation()
        }
      } else {
        console.error('Invalid response structure:', tokenData)
        const errorMsg =
          tokenData?.message ||
          tokenData?.error ||
          'Invalid response from server. Missing customer_access_token or user_data.'
        throw new Error(errorMsg)
      }
    } catch (error: any) {
      let errorMessage = 'OTP verification failed'

      if (error.response?.data) {
        const errorData = error.response.data

        if (errorData.message) {
          errorMessage = typeof errorData.message === 'string' ? errorData.message : errorMessage
        } else if (errorData.error) {
          if (typeof errorData.error === 'string') {
            errorMessage = errorData.error
          } else if (errorData.error.mobile_no?.[0]) {
            errorMessage = errorData.error.mobile_no[0]
          } else if (errorData.error.otp?.[0]) {
            errorMessage = errorData.error.otp[0]
          }
        } else if (typeof errorData === 'string' && errorData.includes('Permission denied')) {
          errorMessage = 'Server error. Please try again later or contact support.'
        }
      } else if (error.message) {
        errorMessage = error.message
      }

      throw new Error(errorMessage)
    }
  }

  async function refreshToken(): Promise<AuthResponse> {
    try {
      const response = await authApi.refreshToken()

      if (response.error) {
        const errorMsg =
          typeof response.error === 'string'
            ? response.error
            : (response.error as any)?.message || 'Token refresh failed'
        throw new Error(errorMsg)
      }

      if (response.data?.customer_access_token && response.data?.user_data) {
        const authResponse = response.data
        setTokens(
          authResponse.customer_access_token,
          authResponse.user_data,
          authResponse.refresh_token
        )
        return authResponse
      }

      if ((response as any)?.customer_access_token && (response as any)?.user_data) {
        const authResponse = response as any as AuthResponse
        setTokens(
          authResponse.customer_access_token,
          authResponse.user_data,
          authResponse.refresh_token
        )
        return authResponse
      }

      throw new Error('Invalid refresh response. Missing customer_access_token or user_data.')
    } catch (error: any) {
      const errorMessage = error?.message || ''
      if (
        errorMessage &&
        !errorMessage.includes('No refresh token') &&
        !errorMessage.includes('No refresh token available')
      ) {
        clearTokens()
      }
      throw error
    }
  }

  async function initializeAuth(): Promise<boolean> {
    // Always sync from storage first so UI matches token/user in sessionStorage/localStorage
    const storedToken =
      localStorage.getItem(ACCESS_TOKEN_KEY) || sessionStorage.getItem(ACCESS_TOKEN_KEY)
    if (storedToken && !isTokenExpired(storedToken)) {
      accessToken.value = storedToken
    }

    const storedUser = localStorage.getItem('user')
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser)
        user.value = userData
      } catch (error) {
        console.warn('Failed to parse stored user data:', error)
        localStorage.removeItem('user')
      }
    }

    // Logged in if we have valid token and user
    if (accessToken.value && user.value) {
      return true
    }
    if (!user.value) {
      return false
    }

    const currentToken =
      accessToken.value ||
      localStorage.getItem(ACCESS_TOKEN_KEY) ||
      sessionStorage.getItem(ACCESS_TOKEN_KEY)

    if (currentToken && !isTokenExpired(currentToken)) {
      accessToken.value = currentToken
      return true
    }

    try {
      await refreshToken()
      return true
    } catch (error: any) {
      const errorMessage = error?.message || ''
      const refreshTokenValue = getRefreshToken()

      if (
        refreshTokenValue ||
        (!errorMessage.includes('No refresh token') &&
          !errorMessage.includes('No refresh token available') &&
          !errorMessage.includes('Unauthenticated'))
      ) {
        clearTokens()
      } else {
        accessToken.value = null
        localStorage.removeItem(ACCESS_TOKEN_KEY)
        sessionStorage.removeItem(ACCESS_TOKEN_KEY)
      }
      return false
    }
  }

  async function logout(): Promise<void> {
    try {
      await authApi.logout()
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      clearActiveReservation()
      clearTokens()
      const current = router.currentRoute.value
      if (current.query?.reservation_id) {
        const q = { ...current.query }
        delete q.reservation_id
        await router.replace({ path: current.path, query: q })
      }
      // Stay on current URL (e.g. smart-dine-in menu with tenant, table_no, token_validate); only reservation_id was cleared
    }
  }

  function setActiveReservation(
    reservationId: string,
    reservationInfo?: { tenantId: string; tableNo: string; reservationNumber?: string }
  ): void {
    activeReservationId.value = reservationId
    localStorage.setItem('active_reservation_id', reservationId)

    // Store full reservation info for quick access (reservationNumber used for check_reservation_host)
    if (reservationInfo) {
      const payload: Record<string, string> = {
        tenantId: reservationInfo.tenantId,
        tableNo: reservationInfo.tableNo,
        reservationId,
      }
      if (reservationInfo.reservationNumber) {
        payload.reservationNumber = reservationInfo.reservationNumber
      }
      localStorage.setItem('active_reservation_info', JSON.stringify(payload))
    }
  }

  function clearActiveReservation(): void {
    activeReservationId.value = null
    localStorage.removeItem('active_reservation_id')
    localStorage.removeItem('active_reservation_info')
  }

  async function checkCustomerMobile(data: OtpRequest): Promise<ApiResponse> {
    try {
      return await authApi.checkCustomerMobile(data)
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to check customer mobile')
    }
  }

  async function resendOTP(data: OtpRequest): Promise<void> {
    try {
      await authApi.resendOTP(data)
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Failed to resend OTP')
    }
  }

  async function checkCustomerInfoValidity(data: {
    mobile_no: string
    first_name: string
    last_name: string
    email?: string
  }): Promise<void> {
    try {
      const response = await authApi.checkCustomerInfoValidity({
        ...data,
        refresh_required: true,
      })

      // Check if response has an error
      if (response.error) {
        const errorMsg =
          typeof response.error === 'string'
            ? response.error
            : response.error.mobile_no?.[0] ||
              response.error.first_name?.[0] ||
              response.error.last_name?.[0] ||
              'Registration failed'
        throw new Error(errorMsg)
      }

      // Legacy format: response contains customer_access_token and user_data directly
      const tokenData = response as any

      // Check if response is a plain string error
      if (typeof tokenData === 'string') {
        throw new Error(tokenData.trim())
      }

      // Check if response has error field
      if (tokenData?.error) {
        const errorMsg =
          typeof tokenData.error === 'string'
            ? tokenData.error
            : tokenData.error.message || 'An error occurred'
        throw new Error(errorMsg)
      }

      if (tokenData?.customer_access_token && tokenData?.user_data) {
        setTokens(tokenData.customer_access_token, tokenData.user_data, tokenData.refresh_token)
      } else {
        const errorMsg =
          tokenData?.message ||
          tokenData?.error ||
          'Invalid response from server. Missing customer_access_token or user_data.'
        throw new Error(errorMsg)
      }
    } catch (error: any) {
      let errorMessage = 'Registration failed'

      if (error.response?.data) {
        const errorData = error.response.data

        if (errorData.message) {
          errorMessage = typeof errorData.message === 'string' ? errorData.message : errorMessage
        } else if (errorData.error) {
          if (typeof errorData.error === 'string') {
            errorMessage = errorData.error
          } else if (errorData.error.mobile_no?.[0]) {
            errorMessage = errorData.error.mobile_no[0]
          } else if (errorData.error.first_name?.[0]) {
            errorMessage = errorData.error.first_name[0]
          } else if (errorData.error.last_name?.[0]) {
            errorMessage = errorData.error.last_name[0]
          }
        }
      } else if (error.message) {
        errorMessage = error.message
      }

      throw new Error(errorMessage)
    }
  }

  function setAuthFromReservationValidation(data: {
    active_reservation: {
      customer_access_token: string
      active_reservation_id: number
      tenant_id: string
      table_no: string
      reservation_numer?: string
      reservation_number?: string
    }
    user_data: User
  }): void {
    // Set tokens and user
    setTokens(data.active_reservation.customer_access_token, data.user_data)

    // Set active reservation with full info (reservation_number for check_reservation_host)
    const reservationId = String(data.active_reservation.active_reservation_id)
    const reservationNumber =
      data.active_reservation.reservation_number ?? data.active_reservation.reservation_numer
    setActiveReservation(reservationId, {
      tenantId: data.active_reservation.tenant_id,
      tableNo: data.active_reservation.table_no,
      ...(reservationNumber ? { reservationNumber: String(reservationNumber) } : {}),
    })
  }

  /**
   * Set auth and active reservation from make_instant_reservation response (guest flow).
   * Call after successful make_instant_reservation when user has no account.
   */
  function setAuthFromGuestReservation(
    data: {
      customer_access_token: string
      user_data: User
      active_reservation_id: string | number
    },
    tenantId: string,
    tableNo: string,
    reservationNumber?: string
  ): void {
    const uid = data.user_data.user_id
    const userData: User = {
      ...data.user_data,
      user_id: typeof uid === 'number' ? String(uid) : uid,
    }
    setTokens(data.customer_access_token, userData)
    setActiveReservation(String(data.active_reservation_id), {
      tenantId,
      tableNo,
      ...(reservationNumber ? { reservationNumber: String(reservationNumber) } : {}),
    })
  }

  function extractUserFromResponse(
    response: ApiResponse<User> | User | Record<string, unknown>
  ): User | null {
    if (!response || typeof response !== 'object') return null

    const payload = response as Record<string, unknown>
    if (payload.error) return null

    const candidates = [payload, payload.data, payload.user_data]
    for (const candidate of candidates) {
      if (!candidate || typeof candidate !== 'object') continue
      const record = candidate as Record<string, unknown>
      if (typeof record.first_name === 'string') {
        const uid = record.user_id
        const nextUser: User = {
          user_id: typeof uid === 'number' ? String(uid) : String(uid ?? ''),
          first_name: record.first_name,
        }
        if (typeof record.last_name === 'string') nextUser.last_name = record.last_name
        if (typeof record.email === 'string') nextUser.email = record.email
        if (typeof record.mobile_no === 'string') nextUser.mobile_no = record.mobile_no
        return nextUser
      }
    }

    return null
  }

  function persistUser(nextUser: User): void {
    user.value = nextUser
    localStorage.setItem('user', JSON.stringify(nextUser))
  }

  async function fetchCurrentUser(): Promise<void> {
    const response = await authApi.getCurrentUser()
    if (response.error) {
      throw new Error(parseApiError(response).message)
    }

    const nextUser = extractUserFromResponse(response)
    if (nextUser) {
      persistUser(nextUser)
    }
  }

  async function updateProfile(data: UpdateProfileRequest): Promise<string> {
    const response = await authApi.updateCurrentUser(data)
    if (response.error) {
      const fieldError =
        parseFieldError(response, 'first_name') ||
        parseFieldError(response, 'last_name') ||
        parseFieldError(response, 'email')
      throw new Error(fieldError || parseApiError(response).message)
    }

    const nextUser = extractUserFromResponse(response)
    if (nextUser) {
      persistUser(nextUser)
    } else if (user.value) {
      persistUser({
        ...user.value,
        first_name: data.first_name.trim(),
        last_name: data.last_name.trim(),
        email: data.email.trim(),
      })
    }

    return typeof response.success === 'string' && response.success.trim()
      ? response.success
      : 'Profile updated successfully.'
  }

  return {
    accessToken,
    user,
    activeReservationId,
    isAuthenticated,
    fullUserName,
    setTokens,
    clearTokens,
    sendOTP,
    verifyOTP,
    refreshToken,
    initializeAuth,
    logout,
    setActiveReservation,
    clearActiveReservation,
    setAuthFromReservationValidation,
    setAuthFromGuestReservation,
    checkCustomerMobile,
    resendOTP,
    checkCustomerInfoValidity,
    fetchCurrentUser,
    updateProfile,
  }
})
